import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Building2, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Copy,
  ChevronRight,
  ChevronLeft,
  Percent,
  Settings,
  AlertCircle,
  RefreshCw,
  Home,
  ArrowLeftRight,
  Check,
  FileText,
  CreditCard,
  Sparkles
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ProductsTable from './ProductsTable';
import { groupByZone } from './productZones';
import { generateProductName } from './productName';
import ProductExcelImport from './ProductExcelImport';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ================ FUNCȚII HELPER PENTRU FORMAT ROMÂNESC ================

// Formatează număr pentru afișare (punct -> virgulă pentru zecimale)
const formatRO = (value) => {
  if (value === null || value === undefined || value === '') return '';
  return String(value).replace('.', ',');
};

// Formatează sumă cu separator de mii (100.000, 500.000)
const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseInt(value.replace(/\./g, ''), 10) : value;
  if (isNaN(num)) return '';
  return num.toLocaleString('ro-RO');
};

// Parsează sumă (elimină separatorii de mii)
const parseAmount = (value) => {
  if (!value || value === '') return 0;
  // Elimină punctele (separatori de mii) și convertește la număr
  const cleaned = String(value).replace(/\./g, '').replace(/,/g, '');
  const num = parseInt(cleaned, 10);
  return isNaN(num) ? 0 : num;
};

// Parsează input românesc (acceptă atât virgulă cât și punct)
const parseRO = (value) => {
  if (!value || value === '') return null;
  // Înlocuiește virgula cu punct pentru calcul
  const normalized = String(value).replace(',', '.');
  const num = parseFloat(normalized);
  return isNaN(num) ? null : num;
};

// Validează input pentru procente (permite numere, virgulă și punct)
const isValidPercentInput = (value) => {
  if (value === '') return true;
  // Permite: cifre, o singură virgulă SAU un singur punct
  return /^[0-9]*[,.]?[0-9]*$/.test(value);
};

// Validează input pentru sume (permite numere și punct ca separator de mii)
const isValidAmountInput = (value) => {
  if (value === '') return true;
  // Permite: cifre și puncte pentru separatorul de mii
  return /^[0-9.]*$/.test(value);
};

// Validare input cu zecimale (virgulă sau punct)
const isValidDecimalInput = (value) => {
  if (value === '') return true;
  return /^[0-9]*[,.]?[0-9]*$/.test(value);
};

// Formatare sumă cu zecimale (pentru costuri: RNPM, comision admin, etc.)
const formatDecimal = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(String(value).replace(',', '.')) : value;
  if (isNaN(num)) return '';
  if (num % 1 === 0) return num.toString();
  return String(num).replace('.', ',');
};

// Input controlat pentru sume cu zecimale - ține textul brut și parsează la blur
const DecimalInput = ({ value, onChange, placeholder, ...props }) => {
  const [localValue, setLocalValue] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);
  
  React.useEffect(() => {
    if (!isFocused) {
      setLocalValue(formatDecimal(value));
    }
  }, [value, isFocused]);
  
  return (
    <Input
      {...props}
      type="text"
      inputMode="decimal"
      placeholder={placeholder}
      value={isFocused ? localValue : formatDecimal(value)}
      onFocus={() => {
        setIsFocused(true);
        setLocalValue(formatDecimal(value));
      }}
      onChange={(e) => {
        const v = e.target.value;
        if (isValidDecimalInput(v)) {
          setLocalValue(v);
        }
      }}
      onBlur={() => {
        setIsFocused(false);
        const parsed = parseRO(localValue);
        onChange(parsed !== null ? parsed : 0);
      }}
    />
  );
};

// ================ NOUA SCHEMĂ PRODUSE BANCARE ================

// Tipuri de credit
const CREDIT_TYPES = [
  { id: 'achizitie', label: 'Achiziție', labelRO: 'Credit Ipotecar Achiziție' },
  { id: 'refinantare', label: 'Refinanțare', labelRO: 'Credit Ipotecar Refinanțare' }
];

// Tipuri de dobândă
const INTEREST_TYPES = [
  { id: 'fixed_then_variable', label: 'Dobândă fixă + variabilă', description: 'Perioada fixă (2/3/5/10 ani sau toată perioada), apoi IRCC + marjă' },
  { id: 'variable', label: 'Dobândă variabilă din prima zi', description: 'IRCC + marjă din start' }
];

// Perioade fixe disponibile
const FIXED_PERIODS = [
  { years: 2, label: '2 ani' },
  { years: 3, label: '3 ani' },
  { years: 5, label: '5 ani' },
  { years: 10, label: '10 ani' },
  { years: 0, label: 'Toată perioada', isFullPeriod: true }
];

// Clase energetice
const ENERGY_CLASSES = [
  { id: 'A', label: 'Clasa A' },
  { id: 'BC', label: 'Clasa B/C' },
  { id: 'GREEN', label: 'Verde / nZEB' }
];

// Tipuri de discount/reguli
// Template pentru produs nou (model direct - fără discounturi)
const createEmptyProduct = () => ({
  bank_id: '',
  product_name: '',
  credit_type: 'achizitie',
  currency: 'RON',
  min_amount: 50000,
  max_amount: 2500000,
  min_period_months: 12,
  max_period_months: 360,
  energy_class: 'BC',
  salary_transfer: null,
  min_income_required: null,
  interest_type: 'fixed_then_variable',
  fixed_years: 5,
  fixed_rate: null,
  variable_reference: 'IRCC',
  variable_margin: null,
  costs: {
    analysis_fee: 900,
    admin_fee_monthly: 0,
    admin_fee_annual: 0,
    early_repayment_fee: 1.00,
    life_insurance_percent: 0.026,
    pad_fee: 130,
    rnpm_fee: 88.4,
    apartment_evaluation: 0,
    house_evaluation: 0,
    voucher_mortgage: 0,
    voucher_refinance: 0
  },
  notes: '',
  active: true
});

// Costuri default pentru o bancă nouă
const DEFAULT_BANK_COSTS = {
  analysis_fee: 0,
  monthly_admin_fee: 0,
  early_repayment_fee: 1,
  life_insurance_percent: 0.026,
  pmd_fee: 130,
  fngcimm_fee: 0,
  voucher_mortgage: 0,
  voucher_refinance: 0
};

// ================================
// MAIN COMPONENT
// ================================

const BanksManagerV2 = () => {
  // State
  const [banks, setBanks] = useState([]);
  const [products, setProducts] = useState([]);
  const [groupedProducts, setGroupedProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [bulkCreatingRefinance, setBulkCreatingRefinance] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [productFilter, setProductFilter] = useState('all');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('finbro_admin_view_mode') || 'cards');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dialog states
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteBankId, setDeleteBankId] = useState(null);
  
  // Bank dialog
  const [isBankDialogOpen, setIsBankDialogOpen] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  
  // Settings
  const [irccRate, setIrccRate] = useState(5.55);
  const [irccHist, setIrccHist] = useState({ ircc_min_historical: '', ircc_max_historical: '', ircc_max_period: '', ircc_source: '' });
  const [irccUpdatedAt, setIrccUpdatedAt] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Asigurare facultativă procente globale
  const [propInsWithValue, setPropInsWithValue] = useState(0.085);

  // State pentru bănci expandate (folder toggle)
  const [expandedBanks, setExpandedBanks] = useState({});
  const [propInsWithoutValue, setPropInsWithoutValue] = useState(0.110);

  // Copy product feature states
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [copySourceProduct, setCopySourceProduct] = useState(null);

  // Fetch functions
  const fetchBanks = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/products/banks`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBanks(response.data.banks || []);
    } catch (err) {
      console.error('Error fetching banks:', err);
    }
  }, []);

  const hasProductsRef = useRef(false);
  const fetchProducts = useCallback(async () => {
    // Spinner-ul apare doar la prima încărcare; la reîmprospătări lista rămâne pe ecran
    // (altfel se pierde poziția de scroll după fiecare salvare/ștergere)
    if (!hasProductsRef.current) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/products/?grouped=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const groups = response.data.groups || [];
      setGroupedProducts(groups);
      hasProductsRef.current = groups.length > 0;
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Eroare la încărcarea produselor');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const settingsRes = await axios.get(`${API_URL}/api/admin/settings`, { headers: { Authorization: `Bearer ${token}` } });
      setIrccRate(settingsRes.data.ircc_rate || 5.55);
      setIrccUpdatedAt(settingsRes.data.ircc_updated_at);
      setIrccHist({ ircc_min_historical: settingsRes.data.ircc_min_historical ?? '', ircc_max_historical: settingsRes.data.ircc_max_historical ?? '', ircc_max_period: settingsRes.data.ircc_max_period || '', ircc_source: settingsRes.data.ircc_source || '' });
      
      // Asigurare facultativă procente
      const defaultCosts = settingsRes.data.default_costs || {};
      setPropInsWithValue(defaultCosts.property_insurance_with_value_percent || 0.085);
      setPropInsWithoutValue(defaultCosts.property_insurance_without_value_percent || 0.110);
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  }, []);

  useEffect(() => {
    fetchBanks();
    fetchProducts();
    fetchSettings();
  }, [fetchBanks, fetchProducts, fetchSettings]);

  const [reseedingBank, setReseedingBank] = useState(null);

  const handleReseedBank = async (bankName) => {
    if (!window.confirm(`Aceasta va reseta TOATE produsele ${bankName} la valorile din cod. Continui?`)) return;
    setReseedingBank(bankName);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${API_URL}/api/products/reseed-bank/${encodeURIComponent(bankName)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`✅ ${bankName}: ${resp.data.deleted} produse șterse, ${resp.data.created} produse noi create`);
      fetchProducts();
    } catch (err) {
      alert(`❌ Eroare: ${err.response?.data?.detail || err.message}`);
    } finally {
      setReseedingBank(null);
    }
  };

  // Bank handlers
  const openBankForEdit = (bank) => {
    const grp = groupedProducts.find(g => g.bank?.id === bank.id);
    const firstProd = (grp?.products || []).find(p => p.min_amount != null || p.max_amount != null);
    setEditingBank({
      ...bank,
      min_amount: bank.min_amount ?? firstProd?.min_amount ?? '',
      max_amount: bank.max_amount ?? firstProd?.max_amount ?? '',
    });
    setIsBankDialogOpen(true);
  };

  const handleSaveBank = async () => {
    try {
      const token = localStorage.getItem('token');
      const bankData = {
        name: editingBank.name,
        full_name: editingBank.full_name,
        cui: editingBank.cui,
        registration_number: editingBank.registration_number,
        phone: editingBank.phone,
        email: editingBank.email,
        address: editingBank.address,
        website: editingBank.website,
        is_active: editingBank.is_active !== false,
        costs: editingBank.costs || {},
        costs_refinantare: editingBank.costs_refinantare || {},
        min_amount: editingBank.min_amount === '' || editingBank.min_amount == null ? null : parseInt(editingBank.min_amount, 10),
        max_amount: editingBank.max_amount === '' || editingBank.max_amount == null ? null : parseInt(editingBank.max_amount, 10),
      };
      
      if (editingBank.id) {
        await axios.put(
          `${API_URL}/api/products/banks/${editingBank.id}`,
          bankData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Propagă suma minimă/maximă la toate produsele băncii (sunt comune pe bancă)
        if (bankData.min_amount != null || bankData.max_amount != null) {
          const grp = groupedProducts.find(g => g.bank?.id === editingBank.id);
          const prods = grp?.products || [];
          await Promise.all(prods.map(p => axios.put(
            `${API_URL}/api/products/${p.id}`,
            { min_amount: bankData.min_amount, max_amount: bankData.max_amount },
            { headers: { Authorization: `Bearer ${token}` } }
          ).catch(() => null)));
        }
      } else {
        await axios.post(
          `${API_URL}/api/products/banks`,
          bankData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      setIsBankDialogOpen(false);
      setEditingBank(null);
      fetchBanks();
      fetchProducts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Eroare la salvarea băncii');
    }
  };

  const handleDeleteBank = async (bankId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/products/banks/${bankId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchBanks();
      fetchProducts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Eroare la ștergerea băncii');
    }
  };

  // Product handlers
  const handleOpenWizard = (product = null) => {
    const defaultCosts = createEmptyProduct().costs;
    
    if (product) {
      setEditingProduct({ 
        ...createEmptyProduct(),
        ...product,
        min_amount: product.min_amount || 50000,
        max_amount: product.max_amount || 2500000,
        min_period_months: product.min_period_months || 12,
        max_period_months: product.max_period_months || 360,
        currency: product.currency || 'RON',
        fixed_rate: product.fixed_rate ?? product.fixed_interest_base ?? null,
        variable_margin: product.variable_margin ?? product.variable_margin_base ?? null,
        costs: { ...defaultCosts, ...(product.costs || {}) }
      });
    } else {
      setEditingProduct(createEmptyProduct());
    }
    setWizardStep(1);
    setIsWizardOpen(true);
  };

  const handleSaveProduct = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const fixedRate = parseRO(editingProduct.fixed_rate);
      const variableMargin = parseRO(editingProduct.variable_margin);
      
      const productData = {
        ...editingProduct,
        fixed_rate: fixedRate,
        fixed_interest_base: fixedRate,
        variable_margin: variableMargin,
        variable_margin_base: variableMargin,
        rate_lookup: [{
          conditions: {},
          fixed_rate: fixedRate,
          variable_margin: variableMargin
        }],
        discounts: [],
        down_payment_conditions: { enabled: false, conditions: [] },
        costs: null  // Produsele moștenesc costurile de la bancă
      };
      
      if (editingProduct.id) {
        await axios.put(
          `${API_URL}/api/products/${editingProduct.id}`,
          productData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_URL}/api/products/`,
          productData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      setIsWizardOpen(false);
      if (editingProduct?.bank_id) {
        setExpandedBanks(prev => ({...prev, [editingProduct.bank_id]: true}));
      }
      setEditingProduct(null);
      fetchProducts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Eroare la salvarea produsului');
    }
  };

  const handleDuplicateProduct = async (productId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/products/${productId}/duplicate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchProducts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Eroare la duplicarea produsului');
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/products/${productId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDeleteId(null);
      fetchProducts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Eroare la ștergerea produsului');
    }
  };

  // Settings handlers
  const handleSaveIRCC = async () => {
    setSavingSettings(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/admin/ircc?rate=${irccRate}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchSettings();
    } catch (err) {
      setError(err.response?.data?.detail || 'Eroare la salvarea IRCC');
    } finally {
      setSavingSettings(false);
    }
  };

  // Salvează datele istorice IRCC pentru scenariul de creștere a dobânzii (Anexa 2, partea B)
  const handleSaveIrccHist = async () => {
    setSavingSettings(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/admin/settings`, {
        ircc_min_historical: irccHist.ircc_min_historical === '' ? null : parseFloat(irccHist.ircc_min_historical),
        ircc_max_historical: irccHist.ircc_max_historical === '' ? null : parseFloat(irccHist.ircc_max_historical),
        ircc_max_period: irccHist.ircc_max_period || null,
        ircc_source: irccHist.ircc_source || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchSettings();
    } catch (err) {
      setError(err.response?.data?.detail || 'Eroare la salvarea datelor IRCC istorice');
    } finally {
      setSavingSettings(false);
    }
  };

  // Salvează procentele asigurării facultative
  const handleSavePropertyInsurance = async () => {
    setSavingSettings(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/admin/settings`,
        {
          default_costs: {
            property_insurance_with_value_percent: propInsWithValue,
            property_insurance_without_value_percent: propInsWithoutValue
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchSettings();
    } catch (err) {
      setError(err.response?.data?.detail || 'Eroare la salvarea setărilor');
    } finally {
      setSavingSettings(false);
    }
  };

  // Update product field helper
  const updateProduct = (field, value) => {
    setEditingProduct(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Încadrare avans (down_payment_min/max). Selectorul e derivat PUR din date:
  // nu scrie nimic automat la deschidere; scrie doar când adminul alege manual.
  const DP_PRESETS = [
    { key: 'unset', label: 'Nespecificat (se ia din denumire)', min: null, max: null },
    { key: '15', label: '15%  (15–20%)', min: 15, max: 20 },
    { key: 'lt20', label: '< 20%  (0–20%)', min: 0, max: 20 },
    { key: 'gte20', label: '≥ 20%  (20–100%)', min: 20, max: 100 },
    { key: 'gte25', label: '25%  (≥25% → 25–100%)', min: 25, max: 100 },
    { key: '20_40', label: '20–40%', min: 20, max: 40 },
    { key: 'gt40', label: '≥ 40%  (40–100%)', min: 40, max: 100 },
    { key: 'custom', label: 'Personalizat (min/max manual)', min: null, max: null },
  ];

  // Deduce (min,max) din denumirea produsului — aceleași tipare ca backend-ul
  const parseDpFromName = (name) => {
    const s = (name || '').toLowerCase();
    if (/avans[^0-9]*20\s*%?\s*[-–]\s*40/.test(s)) return { min: 20, max: 40 };
    if (/avans[^0-9]*(>|≥|>=|peste|mai\s*mare[^0-9]*)\s*40/.test(s)) return { min: 40, max: 100 };
    if (/avans[^0-9]*(≥|>=)\s*25/.test(s)) return { min: 25, max: 100 };
    if (/avans[^0-9]*(<|≤|<=|sub|mai\s*mic[^0-9]*)\s*20/.test(s)) return { min: 0, max: 20 };
    if (/avans[^0-9]*(≥|>=|>|peste|mai\s*mare[^0-9]*)\s*20/.test(s) || /avans\s*20\s*%(?!\s*[-–])/.test(s)) return { min: 20, max: 100 };
    if (/avans[^0-9]*15/.test(s)) return { min: 15, max: 20 };
    return null;
  };

  const dpFieldsPair = () => {
    const mn = editingProduct.down_payment_min;
    const mx = editingProduct.down_payment_max;
    return (mn == null && mx == null) ? null : { min: mn, max: mx };
  };
  const dpTitlePair = () => parseDpFromName(editingProduct.product_name);
  const pairToKey = (pair) => {
    if (!pair) return 'unset';
    const hit = DP_PRESETS.find(p => p.key !== 'unset' && p.key !== 'custom' && p.min === pair.min && p.max === pair.max);
    return hit ? hit.key : 'custom';
  };
  const pairLabel = (p) => (p ? `${p.min}%–${p.max}%` : '—');

  // Ce afișează selectorul: câmpurile dacă sunt setate, altfel încadrarea din titlu
  const dpSelectorKey = () => {
    const f = dpFieldsPair();
    if (f) return pairToKey(f);
    const t = dpTitlePair();
    return t ? pairToKey(t) : 'unset';
  };

  // Alarmă: câmpurile setate ȘI titlul are încadrare ȘI diferă
  const dpMismatch = () => {
    const f = dpFieldsPair();
    const t = dpTitlePair();
    if (!f || !t) return null;
    if (f.min === t.min && f.max === t.max) return null;
    return { fields: f, title: t };
  };

  const onDpPresetChange = (key) => {
    if (key === 'custom') {
      setEditingProduct(prev => ({
        ...prev,
        down_payment_min: prev.down_payment_min ?? 0,
        down_payment_max: prev.down_payment_max ?? 100,
      }));
      return;
    }
    const p = DP_PRESETS.find(x => x.key === key);
    setEditingProduct(prev => ({ ...prev, down_payment_min: p.min, down_payment_max: p.max }));
  };

  // Pasul 2: generează denumirea automat din selectoare (identic cu tabelul)
  const genProductName = () => {
    const bankName = banks.find(b => b.id === editingProduct?.bank_id)?.name || '';
    const name = generateProductName(editingProduct || {}, bankName);
    updateProduct('product_name', name);
    toast.success('Denumire generată din selectoare');
  };

  // Costurile care se moștenesc de la bancă
  const INHERITABLE_COSTS = [
    'analysis_fee', 'admin_fee_monthly', 'admin_fee_annual', 'early_repayment_fee',
    'rnpm_fee', 'apartment_evaluation', 'house_evaluation',
    'life_insurance_percent', 'pad_fee', 'property_insurance_percent',
    'pmd_fee', 'fngcimm_fee', 'voucher_mortgage', 'voucher_refinance'
  ];

  // Pre-completează costurile produsului din costurile băncii (pe baza credit_type)
  const inheritBankCosts = (bankId, currentCosts = {}, creditType = null) => {
    const bank = banks.find(b => b.id === bankId);
    if (!bank) return currentCosts;
    
    // Alege setul de costuri: refinanțare dacă există și credit_type e refinantare, altfel ipotecar
    const isRefinantare = creditType === 'refinantare';
    const bankCosts = isRefinantare && bank.costs_refinantare && Object.keys(bank.costs_refinantare).length > 0
      ? { ...bank.costs, ...bank.costs_refinantare }  // Refinanțare: start from ipotecar, override with refinanțare
      : (bank.costs || {});
    
    const merged = { ...currentCosts };
    for (const key of INHERITABLE_COSTS) {
      if (merged[key] === undefined || merged[key] === null || merged[key] === '') {
        if (bankCosts[key] !== undefined && bankCosts[key] !== null) {
          merged[key] = bankCosts[key];
        }
      }
    }
    return merged;
  };

  // Selectare bancă cu moștenire costuri
  const handleBankSelect = (bankId) => {
    setEditingProduct(prev => ({
      ...prev,
      bank_id: bankId,
      bank_name: banks.find(b => b.id === bankId)?.name || '',
      costs: inheritBankCosts(bankId, prev.costs, prev.credit_type)
    }));
  };

  // Format helpers
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Necunoscut';
    return new Date(dateStr).toLocaleDateString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProductTypeLabel = (type) => {
    const ct = CREDIT_TYPES.find(c => c.id === type);
    return ct?.label || type;
  };

  // Wizard navigation (3 pași: 1=Info, 2=Dobânzi, 3=Costuri)
  const canGoNext = () => {
    switch (wizardStep) {
      case 1: return editingProduct?.bank_id && editingProduct?.product_name;
      case 2: {
        if (editingProduct?.interest_type === 'fixed_then_variable') {
          return editingProduct?.fixed_rate != null;
        }
        return editingProduct?.variable_margin != null;
      }
      case 3: return true;
      default: return false;
    }
  };

  const nextStep = () => {
    if (wizardStep < 3 && canGoNext()) {
      setWizardStep(wizardStep + 1);
    }
  };

  const prevStep = () => {
    if (wizardStep > 1) {
      setWizardStep(wizardStep - 1);
    }
  };

  // ================================
  // COPY PRODUCT FEATURE
  // ================================

  // Găsește produsul de Achiziție pentru aceeași bancă
  const findAcquisitionProduct = () => {
    if (!editingProduct?.bank_id) return null;
    
    // Caută în grupurile de produse
    for (const group of groupedProducts) {
      if (group.bank_id === editingProduct.bank_id) {
        const acquisitionProduct = group.products?.find(
          p => p.credit_type === 'achizitie' && p.id !== editingProduct.id
        );
        return acquisitionProduct || null;
      }
    }
    return null;
  };

  // Găsește produsul de Refinanțare pentru aceeași bancă
  const findRefinanceProduct = () => {
    if (!editingProduct?.bank_id) return null;
    
    for (const group of groupedProducts) {
      if (group.bank_id === editingProduct.bank_id) {
        const refinanceProduct = group.products?.find(
          p => p.credit_type === 'refinantare' && p.id !== editingProduct.id
        );
        return refinanceProduct || null;
      }
    }
    return null;
  };

  // Copiază datele din produsul sursă în produsul curent
  const handleCopyFromProduct = (sourceProduct) => {
    if (!sourceProduct) return;
    
    setEditingProduct(prev => ({
      ...prev,
      // Păstrează: id, bank_id, product_name, credit_type
      // Copiază restul
      currency: sourceProduct.currency,
      min_amount: sourceProduct.min_amount,
      max_amount: sourceProduct.max_amount,
      min_period_months: sourceProduct.min_period_months,
      max_period_months: sourceProduct.max_period_months,
      interest_type: sourceProduct.interest_type,
      fixed_years: sourceProduct.fixed_years,
      fixed_rate: sourceProduct.fixed_rate ?? sourceProduct.fixed_interest_base,
      fixed_interest_base: sourceProduct.fixed_rate ?? sourceProduct.fixed_interest_base,
      variable_reference: sourceProduct.variable_reference,
      variable_margin: sourceProduct.variable_margin ?? sourceProduct.variable_margin_base,
      variable_margin_base: sourceProduct.variable_margin ?? sourceProduct.variable_margin_base,
      override_bank_costs: sourceProduct.override_bank_costs,
      costs: sourceProduct.costs ? { ...sourceProduct.costs } : null,
      notes: prev.notes // Păstrează notele proprii
    }));
    
    setShowCopyDialog(false);
  };

  // Sincronizează modificările înapoi la produsul original (Achiziție)
  const handleSyncToProduct = async (targetProduct) => {
    if (!targetProduct || !editingProduct) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Pregătește datele pentru update
      const updateData = {
        currency: editingProduct.currency,
        min_amount: editingProduct.min_amount,
        max_amount: editingProduct.max_amount,
        min_period_months: editingProduct.min_period_months,
        max_period_months: editingProduct.max_period_months,
        interest_type: editingProduct.interest_type,
        fixed_years: editingProduct.fixed_years,
        fixed_rate: parseRO(editingProduct.fixed_rate),
        fixed_interest_base: parseRO(editingProduct.fixed_rate),
        variable_reference: editingProduct.variable_reference,
        variable_margin: parseRO(editingProduct.variable_margin),
        variable_margin_base: parseRO(editingProduct.variable_margin),
        override_bank_costs: editingProduct.override_bank_costs,
        costs: editingProduct.costs
      };
      
      await axios.put(
        `${API_URL}/api/products/${targetProduct.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowSyncDialog(false);
      alert('✅ Produsul de Achiziție a fost actualizat cu succes!');
      fetchProducts(); // Refresh lista
    } catch (err) {
      console.error('Error syncing product:', err);
      alert('❌ Eroare la sincronizare: ' + (err.response?.data?.detail || 'Eroare necunoscută'));
    }
  };

  // Construiește payload-ul pentru un produs de Refinanțare pornind de la unul de Achiziție.
  // Copiază dobânzile din Achiziție; costurile rămân moștenite de la bancă (setul Refinanțare).
  const buildRefinancePayload = (sourceProduct) => ({
    bank_id: sourceProduct.bank_id,
    product_name: sourceProduct.product_name.replace('Achiziție', 'Refinanțare').replace('achiziție', 'refinanțare') || `${sourceProduct.product_name} - Refinanțare`,
    credit_type: 'refinantare',
    currency: sourceProduct.currency || 'RON',
    min_amount: sourceProduct.min_amount,
    max_amount: sourceProduct.max_amount,
    min_period_months: sourceProduct.min_period_months,
    max_period_months: sourceProduct.max_period_months,
    interest_type: sourceProduct.interest_type,
    fixed_years: sourceProduct.fixed_years,
    fixed_rate: sourceProduct.fixed_rate ?? sourceProduct.fixed_interest_base,
    fixed_interest_base: sourceProduct.fixed_rate ?? sourceProduct.fixed_interest_base,
    variable_reference: sourceProduct.variable_reference || 'IRCC',
    variable_margin: sourceProduct.variable_margin ?? sourceProduct.variable_margin_base,
    variable_margin_base: sourceProduct.variable_margin ?? sourceProduct.variable_margin_base,
    product_type: sourceProduct.product_type,
    energy_class: sourceProduct.energy_class,
    override_bank_costs: sourceProduct.override_bank_costs,
    costs: sourceProduct.costs || null,
    use_default_costs: sourceProduct.use_default_costs,
    down_payment_min: sourceProduct.down_payment_min ?? null,
    down_payment_max: sourceProduct.down_payment_max ?? null,
    rate_lookup: sourceProduct.rate_lookup || [],
    variants: sourceProduct.variants || [],
    approval_validity_days: sourceProduct.approval_validity_days,
    life_insurance_optional: sourceProduct.life_insurance_optional,
    notes: `Creat automat din produsul de Achiziție: ${sourceProduct.product_name}`,
    active: sourceProduct.active !== false
  });

  // Creare în masă: pentru toate produsele de Achiziție selectate
  const handleBulkCreateRefinance = async (groupProducts) => {
    const sources = groupProducts.filter(p => selectedProducts.has(p.id) && p.credit_type === 'achizitie');
    if (sources.length === 0) return;
    if (!window.confirm(`Creezi ${sources.length} produse de Refinanțare din produsele de Achiziție selectate?`)) return;
    setBulkCreatingRefinance(true);
    const token = localStorage.getItem('token');
    let ok = 0, fail = 0;
    for (const src of sources) {
      try {
        await axios.post(`${API_URL}/api/products/`, buildRefinancePayload(src), { headers: { Authorization: `Bearer ${token}` } });
        ok++;
      } catch (err) {
        console.error('Bulk refinance error:', src.product_name, err);
        fail++;
      }
    }
    setBulkCreatingRefinance(false);
    setSelectedProducts(prev => {
      const next = new Set(prev);
      sources.forEach(s => next.delete(s.id));
      return next;
    });
    alert(`✅ ${ok} produse de Refinanțare create${fail ? ` · ❌ ${fail} eșuate` : ''}.`);
    fetchProducts();
  };

  // Filtru rapid produse (Toate / Achiziție / Refinanțare / Variabile)
  const matchesProductFilter = (p) => {
    if (productFilter === 'achizitie') return p.credit_type === 'achizitie';
    if (productFilter === 'refinantare') return p.credit_type === 'refinantare';
    return true;
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => {
      const next = new Set(prev);
      next.has(productId) ? next.delete(productId) : next.add(productId);
      return next;
    });
  };

  // Construiește payload pentru Achiziție pornind de la Refinanțare (simetric cu buildRefinancePayload)
  const buildPurchasePayload = (sourceProduct) => ({
    bank_id: sourceProduct.bank_id,
    product_name: sourceProduct.product_name.replace('Refinanțare', 'Achiziție').replace('refinanțare', 'achiziție') || `${sourceProduct.product_name} - Achiziție`,
    credit_type: 'achizitie',
    currency: sourceProduct.currency || 'RON',
    min_amount: sourceProduct.min_amount,
    max_amount: sourceProduct.max_amount,
    min_period_months: sourceProduct.min_period_months,
    max_period_months: sourceProduct.max_period_months,
    interest_type: sourceProduct.interest_type,
    fixed_years: sourceProduct.fixed_years,
    fixed_rate: sourceProduct.fixed_rate ?? sourceProduct.fixed_interest_base,
    fixed_interest_base: sourceProduct.fixed_rate ?? sourceProduct.fixed_interest_base,
    variable_reference: sourceProduct.variable_reference || 'IRCC',
    variable_margin: sourceProduct.variable_margin ?? sourceProduct.variable_margin_base,
    variable_margin_base: sourceProduct.variable_margin ?? sourceProduct.variable_margin_base,
    product_type: sourceProduct.product_type,
    energy_class: sourceProduct.energy_class,
    override_bank_costs: sourceProduct.override_bank_costs,
    costs: sourceProduct.costs || null,
    use_default_costs: sourceProduct.use_default_costs,
    down_payment_min: sourceProduct.down_payment_min ?? null,
    down_payment_max: sourceProduct.down_payment_max ?? null,
    rate_lookup: sourceProduct.rate_lookup || [],
    variants: sourceProduct.variants || [],
    approval_validity_days: sourceProduct.approval_validity_days,
    life_insurance_optional: sourceProduct.life_insurance_optional,
    notes: `Creat automat din produsul de Refinanțare: ${sourceProduct.product_name}`,
    active: sourceProduct.active !== false
  });

  // Bulk: creează Achiziție pentru produsele de Refinanțare selectate
  const handleBulkCreatePurchase = async (groupProducts) => {
    const sources = groupProducts.filter(p => selectedProducts.has(p.id) && p.credit_type === 'refinantare');
    if (sources.length === 0) return;
    if (!window.confirm(`Creezi ${sources.length} produse de Achiziție din produsele de Refinanțare selectate?`)) return;
    setBulkCreatingRefinance(true);
    const token = localStorage.getItem('token');
    let ok = 0, fail = 0;
    for (const src of sources) {
      try {
        await axios.post(`${API_URL}/api/products/`, buildPurchasePayload(src), { headers: { Authorization: `Bearer ${token}` } });
        ok++;
      } catch (err) { console.error('Bulk purchase error:', err); fail++; }
    }
    setBulkCreatingRefinance(false);
    setSelectedProducts(prev => { const n = new Set(prev); sources.forEach(s => n.delete(s.id)); return n; });
    alert(`✅ ${ok} produse de Achiziție create${fail ? ` · ❌ ${fail} eșuate` : ''}.`);
    fetchProducts();
  };

  // Bulk: duplică produsele selectate
  const handleBulkDuplicate = async (groupProducts) => {
    const sources = groupProducts.filter(p => selectedProducts.has(p.id));
    if (sources.length === 0) return;
    if (!window.confirm(`Duplici ${sources.length} produse selectate?`)) return;
    setBulkCreatingRefinance(true);
    const token = localStorage.getItem('token');
    let ok = 0, fail = 0;
    for (const p of sources) {
      try {
        await axios.post(`${API_URL}/api/products/${p.id}/duplicate`, {}, { headers: { Authorization: `Bearer ${token}` } });
        ok++;
      } catch (err) { console.error('Bulk duplicate error:', err); fail++; }
    }
    setBulkCreatingRefinance(false);
    setSelectedProducts(prev => { const n = new Set(prev); sources.forEach(s => n.delete(s.id)); return n; });
    alert(`✅ ${ok} produse duplicate${fail ? ` · ❌ ${fail} eșuate` : ''}.`);
    fetchProducts();
  };

  // Bulk: șterge produsele selectate (soft delete, identic cu ștergerea individuală)
  const handleBulkDelete = async (groupProducts) => {
    const sources = groupProducts.filter(p => selectedProducts.has(p.id));
    if (sources.length === 0) return;
    if (!window.confirm(`Sigur ștergi ${sources.length} produse selectate?`)) return;
    setBulkDeleting(true);
    const token = localStorage.getItem('token');
    let ok = 0, fail = 0;
    for (const p of sources) {
      try {
        await axios.delete(`${API_URL}/api/products/${p.id}`, { headers: { Authorization: `Bearer ${token}` } });
        ok++;
      } catch (err) { console.error('Bulk delete error:', err); fail++; }
    }
    setBulkDeleting(false);
    setSelectedProducts(prev => { const n = new Set(prev); sources.forEach(s => n.delete(s.id)); return n; });
    alert(`🗑️ ${ok} produse șterse${fail ? ` · ❌ ${fail} eșuate` : ''}.`);
    fetchProducts();
  };

  // Selectează / deselectează toate produsele dintr-o bancă
  const toggleSelectAllInGroup = (groupProducts) => {
    const ids = groupProducts.map(p => p.id);
    const allSelected = ids.every(id => selectedProducts.has(id));
    setSelectedProducts(prev => {
      const next = new Set(prev);
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };

  // Creează produs Refinanțare identic din Achiziție
  const handleCreateRefinanceFromPurchase = async (sourceProduct) => {
    if (!sourceProduct) return;
    
    try {
      const token = localStorage.getItem('token');
      const newProductData = buildRefinancePayload(sourceProduct);
      
      await axios.post(
        `${API_URL}/api/products/`,
        newProductData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('✅ Produsul de Refinanțare a fost creat cu succes!');
      fetchProducts(); // Refresh lista
    } catch (err) {
      console.error('Error creating refinance product:', err);
      alert('❌ Eroare la creare: ' + (err.response?.data?.detail || 'Eroare necunoscută'));
    }
  };

  // Creează produs Achiziție identic din Refinanțare
  const handleCreatePurchaseFromRefinance = async (sourceProduct) => {
    if (!sourceProduct) return;
    
    try {
      const token = localStorage.getItem('token');
      const newProductData = buildPurchasePayload(sourceProduct);
      
      await axios.post(
        `${API_URL}/api/products/`,
        newProductData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('✅ Produsul de Achiziție a fost creat cu succes!');
      fetchProducts(); // Refresh lista
    } catch (err) {
      console.error('Error creating purchase product:', err);
      alert('❌ Eroare la creare: ' + (err.response?.data?.detail || 'Eroare necunoscută'));
    }
  };

  // Verifică dacă există produs pereche (Refinanțare pentru Achiziție sau invers)
  const hasPairProduct = (product, group) => {
    if (!group || !group.products) return false;
    const targetType = product.credit_type === 'achizitie' ? 'refinantare' : 'achizitie';
    return group.products.some(p => p.credit_type === targetType && p.id !== product.id);
  };

  // Actualizează pe loc un singur produs, fără reîncărcarea listei (nu se pierde poziția pe ecran)
  const handleProductSaved = useCallback((productId, patch) => {
    setGroupedProducts((prev) => prev.map((group) => ({
      ...group,
      products: (group.products || []).map((p) => (p.id === productId ? { ...p, ...patch } : p)),
    })));
  }, []);

  // Subzonele de dobândă restrânse/extinse — aceeași stare ca în vizualizarea Tabel
  const [collapsedZones, setCollapsedZones] = useState(() => {
    try { return JSON.parse(localStorage.getItem('finbro_admin_zones_collapsed') || '{}'); } catch { return {}; }
  });
  const toggleZone = (bankId, zoneKey) => setCollapsedZones((prev) => {
    const next = { ...prev, [`${bankId}:${zoneKey}`]: !prev[`${bankId}:${zoneKey}`] };
    try { localStorage.setItem('finbro_admin_zones_collapsed', JSON.stringify(next)); } catch { /* ignoră */ }
    return next;
  });

  // Sortează produsele: după ani dobândă fixă, apoi Achiziție înaintea Refinanțării
  const sortProducts = (products) => {
    return [...products].sort((a, b) => {
      // Mai întâi sortează după anii de dobândă fixă
      const yearsA = a.fixed_years || 0;
      const yearsB = b.fixed_years || 0;
      if (yearsA !== yearsB) return yearsA - yearsB;
      
      // Apoi după numele produsului (pentru a grupa produsele similare)
      const nameA = (a.product_name || a.name || '').toLowerCase();
      const nameB = (b.product_name || b.name || '').toLowerCase();
      if (nameA !== nameB) return nameA.localeCompare(nameB);
      
      // În final, Achiziție (achizitie) înaintea Refinanțării (refinantare)
      const typeOrder = { 'achizitie': 0, 'refinantare': 1 };
      return (typeOrder[a.credit_type] || 0) - (typeOrder[b.credit_type] || 0);
    });
  };

  // ================================
  // RENDER
  // ================================

  return (
    <div className="space-y-6">
      <Tabs defaultValue="banks">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="banks" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Bănci
          </TabsTrigger>
          <TabsTrigger value="products" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Produse Bancare
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Setări
          </TabsTrigger>
        </TabsList>

        {/* ================ PRODUCTS TAB ================ */}
        <TabsContent value="products" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Produse Bancare
                  </CardTitle>
                  <CardDescription>
                    Gestionați produsele de credit pentru fiecare bancă
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { fetchBanks(); fetchProducts(); }}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                  <Button onClick={() => handleOpenWizard()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Adaugă Produs
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <span className="text-red-700">{error}</span>
                  <Button variant="ghost" size="sm" onClick={() => setError(null)} className="ml-auto">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {!loading && groupedProducts.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="text-sm text-gray-500 mr-1">Filtru rapid:</span>
                  {[
                    { key: 'all', label: 'Toate' },
                    { key: 'achizitie', label: 'Doar Achiziție' },
                    { key: 'refinantare', label: 'Doar Refinanțare' },
                  ].map(f => (
                    <Button
                      key={f.key}
                      size="sm"
                      variant={productFilter === f.key ? 'default' : 'outline'}
                      onClick={() => setProductFilter(f.key)}
                      data-testid={`product-filter-${f.key}`}
                    >
                      {f.label}
                    </Button>
                  ))}
                  <div className="ml-auto inline-flex rounded-lg border bg-white p-0.5" data-testid="product-view-toggle">
                    <Button size="sm" variant={viewMode === 'cards' ? 'default' : 'ghost'}
                      className="h-8" onClick={() => { setViewMode('cards'); localStorage.setItem('finbro_admin_view_mode', 'cards'); }} data-testid="view-cards">
                      Carduri
                    </Button>
                    <Button size="sm" variant={viewMode === 'table' ? 'default' : 'ghost'}
                      className="h-8" onClick={() => { setViewMode('table'); localStorage.setItem('finbro_admin_view_mode', 'table'); }} data-testid="view-table">
                      Tabel
                    </Button>
                  </div>
                </div>
              )}

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-500">Se încarcă produsele...</p>
                </div>
              ) : groupedProducts.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">Nu există produse bancare încă.</p>
                  <Button onClick={() => handleOpenWizard()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Adaugă primul produs
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {groupedProducts.map(group => {
                    const isExpanded = expandedBanks[group.bank.id] || false;
                    const visibleProducts = group.products.filter(matchesProductFilter);
                    return (
                    <Card key={group.bank.id} className="border-l-4 border-l-blue-500">
                      <CardHeader 
                        className="pb-2 cursor-pointer select-none hover:bg-gray-50 transition-colors"
                        onClick={() => setExpandedBanks(prev => ({...prev, [group.bank.id]: !prev[group.bank.id]}))}
                      >
                        <CardTitle className="text-lg flex items-center gap-2">
                          <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          <Building2 className="h-5 w-5 text-blue-600" />
                          {group.bank.name}
                          <Badge variant="outline" className="ml-2">
                            {group.products.length} {group.products.length === 1 ? 'produs' : 'produse'}
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      {isExpanded && (
                      <CardContent>
                        {/* Buton costuri generale bancă */}
                        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm text-blue-700">
                            <CreditCard className="h-4 w-4" />
                            <span>Costurile standard se aplică automat la toate produsele.</span>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="border-blue-300 text-blue-700 hover:bg-blue-100 whitespace-nowrap"
                            data-testid={`bank-costs-inline-${group.bank.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              openBankForEdit(group.bank);
                            }}
                          >
                            <CreditCard className="h-4 w-4 mr-1.5" />
                            Costuri generale {group.bank.name}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-orange-300 text-orange-700 hover:bg-orange-100 whitespace-nowrap ml-2"
                            disabled={reseedingBank === group.bank.name}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReseedBank(group.bank.name);
                            }}
                          >
                            <RefreshCw className={`h-4 w-4 mr-1.5 ${reseedingBank === group.bank.name ? 'animate-spin' : ''}`} />
                            {reseedingBank === group.bank.name ? 'Se actualizează...' : `Resetare ${group.bank.name}`}
                          </Button>
                          <ProductExcelImport bankName={group.bank.name} onImported={fetchProducts} />
                        </div>

                        {visibleProducts.length === 0 ? (
                          <p className="text-gray-400 text-sm italic">{group.products.length === 0 ? 'Niciun produs definit' : 'Niciun produs pentru filtrul selectat'}</p>
                        ) : (
                          <>
                          {(() => {
                            const selInGroup = visibleProducts.filter(p => selectedProducts.has(p.id));
                            const selAchiz = selInGroup.filter(p => p.credit_type === 'achizitie').length;
                            const selRefin = selInGroup.filter(p => p.credit_type === 'refinantare').length;
                            const allSel = visibleProducts.length > 0 && visibleProducts.every(p => selectedProducts.has(p.id));
                            return (
                              <div className="mb-3 flex flex-wrap items-center gap-2 p-2 rounded-lg bg-slate-50 border border-slate-200">
                                <Checkbox checked={allSel} onCheckedChange={() => toggleSelectAllInGroup(visibleProducts)} data-testid={`select-all-${group.bank.id}`} />
                                <span className="text-sm text-slate-600 mr-1">{selInGroup.length} selectate</span>
                                {selInGroup.length > 0 && (
                                  <>
                                    {selAchiz > 0 && (
                                      <Button size="sm" variant="outline" className="text-amber-700 border-amber-300 hover:bg-amber-50" disabled={bulkCreatingRefinance} onClick={() => handleBulkCreateRefinance(visibleProducts)} data-testid={`bulk-refinance-${group.bank.id}`}>
                                        <ArrowLeftRight className="h-4 w-4 mr-1" /> Refinanțare ({selAchiz})
                                      </Button>
                                    )}
                                    {selRefin > 0 && (
                                      <Button size="sm" variant="outline" className="text-blue-700 border-blue-300 hover:bg-blue-50" disabled={bulkCreatingRefinance} onClick={() => handleBulkCreatePurchase(visibleProducts)} data-testid={`bulk-purchase-${group.bank.id}`}>
                                        <Home className="h-4 w-4 mr-1" /> Achiziție ({selRefin})
                                      </Button>
                                    )}
                                    <Button size="sm" variant="outline" disabled={bulkCreatingRefinance} onClick={() => handleBulkDuplicate(visibleProducts)} data-testid={`bulk-duplicate-${group.bank.id}`}>
                                      <Copy className="h-4 w-4 mr-1" /> Duplică ({selInGroup.length})
                                    </Button>
                                    <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" disabled={bulkDeleting} onClick={() => handleBulkDelete(visibleProducts)} data-testid={`bulk-delete-${group.bank.id}`}>
                                      <Trash2 className="h-4 w-4 mr-1" /> Șterge ({selInGroup.length})
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => setSelectedProducts(prev => { const n = new Set(prev); visibleProducts.forEach(p => n.delete(p.id)); return n; })}>
                                      Deselectează
                                    </Button>
                                  </>
                                )}
                              </div>
                            );
                          })()}
                          {viewMode === 'table' ? (
                            <ProductsTable
                              products={visibleProducts}
                              bank={group.bank}
                              bankProducts={group.products || []}
                              onEdit={handleOpenWizard}
                              onDeleted={fetchProducts}
                              onSaved={handleProductSaved}
                              onReload={fetchProducts}
                            />
                          ) : (
                          <div className="space-y-3">
                            {groupByZone(visibleProducts).map(zone => {
                              const zoneCollapsed = collapsedZones[`${group.bank.id}:${zone.key}`] || false;
                              return (
                              <div key={zone.key} className={`border-l-4 ${zone.color} bg-slate-50/60 rounded-r-lg`}>
                                <button type="button" onClick={() => toggleZone(group.bank.id, zone.key)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-100/70 transition-colors rounded-r-lg"
                                  data-testid={`zone-toggle-${group.bank.id}-${zone.key}`}>
                                  <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${zoneCollapsed ? '' : 'rotate-90'}`} />
                                  <span className="text-sm font-semibold text-gray-700">{zone.label}</span>
                                  <Badge variant="outline" className="text-[10px]" data-testid={`zone-count-${group.bank.id}-${zone.key}`}>
                                    {zone.products.length} {zone.products.length === 1 ? 'produs' : 'produse'}
                                  </Badge>
                                </button>
                                {!zoneCollapsed && (
                                <div className="px-2 pb-2 space-y-3" data-testid={`zone-body-${group.bank.id}-${zone.key}`}>
                                {zone.products.length === 0 ? (
                                  <p className="text-gray-400 text-xs italic px-1">Niciun produs în această categorie</p>
                                ) : zone.products.map(product => (
                              <div 
                                key={product.id} 
                                className="flex flex-col p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                              >
                                <div className="flex items-center justify-between">
                                  <Checkbox
                                    className="mr-3"
                                    checked={selectedProducts.has(product.id)}
                                    onCheckedChange={() => toggleProductSelection(product.id)}
                                    data-testid={`select-product-${product.id}`}
                                  />
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      {product.credit_type === 'achizitie' ? (
                                        <Home className="h-4 w-4 text-green-600" />
                                      ) : (
                                        <ArrowLeftRight className="h-4 w-4 text-blue-600" />
                                      )}
                                      <span className="font-medium">{product.product_name || product.name}</span>
                                      <Badge variant="outline" className="text-xs">
                                        {product.currency || 'RON'}
                                      </Badge>
                                      {!product.active && !product.is_active && (
                                        <Badge variant="destructive" className="text-xs">Inactiv</Badge>
                                      )}
                                    </div>
                                    <div className="text-sm text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                                      <span>
                                        {(() => {
                                          // rate_lookup: arată range-ul de dobânzi
                                          const rl = product.rate_lookup || [];
                                          if (rl.length > 0) {
                                            const fixedRates = rl.map(e => e.fixed_rate).filter(r => r != null && r > 0);
                                            const margins = rl.map(e => e.variable_margin).filter(m => m != null && m > 0);
                                            if (product.interest_type === 'variable') {
                                              return margins.length > 0
                                                ? `Variabil: IRCC + ${formatRO(Math.min(...margins))}% → ${formatRO(Math.max(...margins))}%`
                                                : 'Variabil';
                                            }
                                            if (fixedRates.length > 0) {
                                              const minF = Math.min(...fixedRates);
                                              const maxF = Math.max(...fixedRates);
                                              return minF === maxF
                                                ? `Fix ${product.fixed_years}ani: ${formatRO(minF)}%`
                                                : `Fix ${product.fixed_years}ani: ${formatRO(minF)}% → ${formatRO(maxF)}%`;
                                            }
                                          }
                                          // Fallback: câmpuri vechi
                                          if (product.interest_type === 'fixed_then_variable') {
                                            return `Fix ${product.fixed_years}ani: ${formatRO(product.fixed_rate ?? product.fixed_interest_base) || '-'}%`;
                                          }
                                          return 'Variabil din start';
                                        })()}
                                      </span>
                                      {product.fixed_years !== 0 && (
                                        <span>
                                          {(() => {
                                            const rl = product.rate_lookup || [];
                                            const margins = rl.map(e => e.variable_margin).filter(m => m != null && m > 0);
                                            if (margins.length > 0) {
                                              const minM = Math.min(...margins);
                                              const maxM = Math.max(...margins);
                                              return minM === maxM
                                                ? `Marjă IRCC: +${formatRO(minM)}%`
                                                : `Marjă IRCC: +${formatRO(minM)}% → +${formatRO(maxM)}%`;
                                            }
                                            return `Marjă IRCC: +${formatRO(product.variable_margin ?? product.variable_margin_base) || '-'}%`;
                                          })()}
                                        </span>
                                      )}
                                      {(product.rate_lookup || []).length > 0 && (
                                        <span className="text-blue-600">
                                          {(product.rate_lookup || []).length} variante
                                        </span>
                                      )}
                                      <span>{formatAmount(product.min_amount)} - {formatAmount(product.max_amount)} {product.currency || 'RON'}</span>
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleOpenWizard(product)}
                                      title="Editează"
                                      data-testid={`edit-product-${product.id}`}
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDuplicateProduct(product.id)}
                                      title="Duplică"
                                    >
                                      <Copy className="h-4 w-4" />
                                    </Button>
                                    {/* Buton special: Creează Refinanțare din Achiziție */}
                                    {product.credit_type === 'achizitie' && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                        onClick={() => {
                                          if (window.confirm(`Creezi un produs de Refinanțare identic cu "${product.product_name}"?`)) {
                                            handleCreateRefinanceFromPurchase(product);
                                          }
                                        }}
                                        title="Creează Refinanțare identică"
                                      >
                                        <ArrowLeftRight className="h-4 w-4" />
                                      </Button>
                                    )}
                                    {/* Buton special: Creează Achiziție din Refinanțare */}
                                    {product.credit_type === 'refinantare' && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                        onClick={() => {
                                          if (window.confirm(`Creezi un produs de Achiziție identic cu "${product.product_name}"?`)) {
                                            handleCreatePurchaseFromRefinance(product);
                                          }
                                        }}
                                        title="Creează Achiziție identică"
                                      >
                                        <Home className="h-4 w-4" />
                                      </Button>
                                    )}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-600 hover:text-red-700"
                                      onClick={() => setDeleteId(product.id)}
                                      title="Șterge"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                                
                              </div>
                            ))}
                                </div>
                                )}
                              </div>
                              );
                            })}
                          </div>
                          )}
                          </>
                        )}
                      </CardContent>
                      )}
                    </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ================ BANKS TAB ================ */}
        <TabsContent value="banks" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Bănci ({banks.length})
                  </CardTitle>
                  <CardDescription>
                    Administrați lista de bănci disponibile
                  </CardDescription>
                </div>
                <Button onClick={() => {
                  setEditingBank({ name: '', is_active: true });
                  setIsBankDialogOpen(true);
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Adaugă Bancă
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {banks.map(bank => {
                  const costsCount = bank.costs ? Object.values(bank.costs).filter(v => v !== null && v !== undefined).length : 0;
                  return (
                    <div 
                      key={bank.id} 
                      className="p-4 border rounded-lg hover:bg-gray-50 space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-base">{bank.name}</p>
                          <p className="text-sm text-gray-500">{bank.products_count || 0} produse</p>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openBankForEdit(bank)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-800 hover:bg-red-50"
                            onClick={() => setDeleteBankId(bank.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800"
                        data-testid={`bank-costs-btn-${bank.id}`}
                        onClick={() => openBankForEdit(bank)}
                      >
                        <CreditCard className="h-4 w-4 mr-2" />
                        Costuri standard bancă
                        {costsCount > 0 && (
                          <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded text-[10px] font-bold">{costsCount}</span>
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ================ SETTINGS TAB ================ */}
        <TabsContent value="settings" className="space-y-4">
          {/* IRCC */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="h-5 w-5" />
                Rata IRCC
              </CardTitle>
              <CardDescription>
                Indicele de Referință pentru Creditele Consumatorilor
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Rata IRCC curentă (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={irccRate}
                    onChange={(e) => setIrccRate(parseFloat(e.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ultima actualizare</Label>
                  <div className="p-2 bg-gray-100 rounded text-sm h-10 flex items-center">
                    {formatDate(irccUpdatedAt)}
                  </div>
                </div>
                <div className="flex items-end">
                  <Button onClick={handleSaveIRCC} disabled={savingSettings}>
                    <Save className="h-4 w-4 mr-2" />
                    Salvează IRCC
                  </Button>
                </div>
              </div>
              <div className="border-t pt-4 space-y-2" data-testid="ircc-history-settings">
                <p className="text-sm font-medium">Scenariu creștere dobândă (FEIS / document informativ – Anexa 2, partea B)</p>
                <p className="text-xs text-gray-500">Exemplul de creștere a DAE din FEIS folosește raportul maxim/minim IRCC din intervalul indicat (implicit 1,08% → 6,06%, T3 2021 – T3 2025, adică ×5,61).</p>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div className="space-y-2">
                    <Label>Minim istoric IRCC (%)</Label>
                    <Input type="number" step="0.01" value={irccHist.ircc_min_historical} placeholder="1.08" data-testid="ircc-min-historical"
                      onChange={(e) => setIrccHist({ ...irccHist, ircc_min_historical: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Maxim istoric IRCC (%)</Label>
                    <Input type="number" step="0.01" value={irccHist.ircc_max_historical} placeholder="6.06" data-testid="ircc-max-historical"
                      onChange={(e) => setIrccHist({ ...irccHist, ircc_max_historical: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Interval analizat</Label>
                    <Input value={irccHist.ircc_max_period} placeholder="trimestrul III 2021 și trimestrul III 2025" data-testid="ircc-max-period"
                      onChange={(e) => setIrccHist({ ...irccHist, ircc_max_period: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Sursa</Label>
                    <Input value={irccHist.ircc_source} placeholder="ex. BNR – www.bnr.ro" data-testid="ircc-source"
                      onChange={(e) => setIrccHist({ ...irccHist, ircc_source: e.target.value })} />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleSaveIrccHist} disabled={savingSettings} data-testid="save-ircc-history">
                      <Save className="h-4 w-4 mr-2" />
                      Salvează istoric
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Asigurare Imobil Facultativă */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                🏠 Asigurare Imobil Facultativă
              </CardTitle>
              <CardDescription>
                Procente globale pentru calculul asigurării facultative (valabile pentru toate băncile)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Cu valoare imobil (%)</Label>
                  <Input
                    type="number"
                    step="0.001"
                    value={propInsWithValue}
                    onChange={(e) => setPropInsWithValue(parseFloat(e.target.value) || 0)}
                  />
                  <p className="text-xs text-gray-500">Se aplică la valoarea proprietății</p>
                </div>
                <div className="space-y-2">
                  <Label>Fără valoare imobil (%)</Label>
                  <Input
                    type="number"
                    step="0.001"
                    value={propInsWithoutValue}
                    onChange={(e) => setPropInsWithoutValue(parseFloat(e.target.value) || 0)}
                  />
                  <p className="text-xs text-gray-500">Se aplică la valoarea creditului</p>
                </div>
                <div className="flex items-end">
                  <Button onClick={handleSavePropertyInsurance} disabled={savingSettings}>
                    <Save className="h-4 w-4 mr-2" />
                    Salvează
                  </Button>
                </div>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800 mt-4">
                <strong>ℹ️ Cum funcționează:</strong>
                <ul className="mt-1 list-disc list-inside">
                  <li>Dacă utilizatorul introduce valoarea imobilului → {propInsWithValue}% × valoare imobil</li>
                  <li>Dacă nu introduce valoarea imobilului → {propInsWithoutValue}% × valoare credit</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ================ BANK DIALOG ================ */}
      <Dialog open={isBankDialogOpen} onOpenChange={setIsBankDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBank?.id ? 'Editare Bancă' : 'Bancă Nouă'}</DialogTitle>
            <DialogDescription>
              Introduceți datele complete ale băncii. Aceste informații vor apărea în documentele FEIS.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Nume scurt */}
            <div className="space-y-2">
              <Label>Nume Scurt * <span className="text-gray-400 text-xs">(afișat în aplicație)</span></Label>
              <Input
                value={editingBank?.name || ''}
                onChange={(e) => setEditingBank({...editingBank, name: e.target.value})}
                placeholder="ex: BRD"
              />
            </div>
            
            {/* Nume complet oficial */}
            <div className="space-y-2">
              <Label>Denumire Oficială Completă <span className="text-gray-400 text-xs">(pentru documente)</span></Label>
              <Input
                value={editingBank?.full_name || ''}
                onChange={(e) => setEditingBank({...editingBank, full_name: e.target.value})}
                placeholder="ex: BRD - GROUPE SOCIETE GENERALE SA"
              />
            </div>
            
            {/* CUI și Nr. Înmatriculare */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cod Unic de Înregistrare (CUI)</Label>
                <Input
                  value={editingBank?.cui || ''}
                  onChange={(e) => setEditingBank({...editingBank, cui: e.target.value})}
                  placeholder="ex: 361579"
                />
              </div>
              <div className="space-y-2">
                <Label>Nr. Înmatriculare</Label>
                <Input
                  value={editingBank?.registration_number || ''}
                  onChange={(e) => setEditingBank({...editingBank, registration_number: e.target.value})}
                  placeholder="ex: J40/608/1991"
                />
              </div>
            </div>
            
            {/* Telefon și Email */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telefon Contact</Label>
                <Input
                  value={editingBank?.phone || ''}
                  onChange={(e) => setEditingBank({...editingBank, phone: e.target.value})}
                  placeholder="ex: *2622"
                />
              </div>
              <div className="space-y-2">
                <Label>Email Contact</Label>
                <Input
                  type="email"
                  value={editingBank?.email || ''}
                  onChange={(e) => setEditingBank({...editingBank, email: e.target.value})}
                  placeholder="ex: contact@brd.ro"
                />
              </div>
            </div>
            
            {/* Adresa și Website */}
            <div className="space-y-2">
              <Label>Adresa Sediului Social</Label>
              <Input
                value={editingBank?.address || ''}
                onChange={(e) => setEditingBank({...editingBank, address: e.target.value})}
                placeholder="ex: Bd. Ion Mihalache nr. 1-7, Sector 1, București"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Website</Label>
              <Input
                value={editingBank?.website || ''}
                onChange={(e) => setEditingBank({...editingBank, website: e.target.value})}
                placeholder="ex: www.brd.ro"
              />
            </div>

            {/* Sumă minimă / maximă credit — comune pe bancă (se aplică tuturor produselor) */}
            <div className="border-t pt-4 mt-2">
              <h3 className="font-semibold text-base mb-2">Sumă credit (comună pe bancă)</h3>
              <p className="text-xs text-gray-500 mb-2">Se aplică automat tuturor produselor acestei bănci. Astfel nu mai completezi sumă în tabelul de produse.</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sumă minimă credit</Label>
                  <Input type="number" value={editingBank?.min_amount ?? ''}
                    onChange={(e) => setEditingBank({...editingBank, min_amount: e.target.value})}
                    placeholder="ex: 50000" data-testid="bank-min-amount" />
                </div>
                <div className="space-y-2">
                  <Label>Sumă maximă credit</Label>
                  <Input type="number" value={editingBank?.max_amount ?? ''}
                    onChange={(e) => setEditingBank({...editingBank, max_amount: e.target.value})}
                    placeholder="ex: 2500000" data-testid="bank-max-amount" />
                </div>
              </div>
            </div>

            {/* ================ COSTURI BANCĂ - 2 COLOANE ================ */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Costuri Standard Bancă
                </h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="copy-costs-to-refinantare"
                  onClick={() => {
                    setEditingBank(prev => ({
                      ...prev,
                      costs_refinantare: { ...(prev?.costs || {}) }
                    }));
                  }}
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copiază Ipotecar → Refinanțare
                </Button>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Setați costurile o singură dată. Se moștenesc automat la toate produsele băncii.
              </p>

              {/* Header coloane */}
              <div className="grid grid-cols-2 gap-6 mb-3">
                <div className="text-center font-semibold text-blue-700 bg-blue-50 rounded-lg py-2">
                  Credit Ipotecar (Achiziție)
                </div>
                <div className="text-center font-semibold text-green-700 bg-green-50 rounded-lg py-2">
                  Refinanțare
                </div>
              </div>

              {/* Comisioane */}
              <h4 className="font-medium text-gray-700 mb-2 mt-4">Comisioane</h4>
              {[
                { key: 'analysis_fee', label: 'Comision analiză dosar', unit: 'RON', step: '1', placeholder: '900' },
                { key: 'admin_fee_monthly', label: 'Comision admin lunar', unit: 'RON', step: '1', placeholder: '25' },
                { key: 'early_repayment_fee', label: 'Rambursare anticipată', unit: '%', step: '0.01', placeholder: '1.00' },
              ].map(({ key, label, unit, step, placeholder }) => (
                <div key={key} className="grid grid-cols-2 gap-6 mb-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">{label} ({unit})</Label>
                    <Input type="number" step={step}
                      value={editingBank?.costs?.[key] ?? ''}
                      onChange={(e) => setEditingBank({...editingBank, costs: {...editingBank?.costs, [key]: e.target.value === '' ? null : parseFloat(e.target.value)}})}
                      placeholder={placeholder} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">{label} ({unit})</Label>
                    <Input type="number" step={step}
                      value={editingBank?.costs_refinantare?.[key] ?? ''}
                      onChange={(e) => setEditingBank({...editingBank, costs_refinantare: {...editingBank?.costs_refinantare, [key]: e.target.value === '' ? null : parseFloat(e.target.value)}})}
                      placeholder={placeholder} />
                  </div>
                </div>
              ))}

              {/* Taxe */}
              <h4 className="font-medium text-gray-700 mb-2 mt-4">Taxe</h4>
              {[
                { key: 'rnpm_fee', label: 'RNPM', unit: 'RON', step: '0.01', placeholder: '88.40' },
                { key: 'apartment_evaluation', label: 'Evaluare apartament', unit: 'RON', step: '1', placeholder: '350' },
                { key: 'house_evaluation', label: 'Evaluare casă', unit: 'RON', step: '1', placeholder: '500' },
              ].map(({ key, label, unit, step, placeholder }) => (
                <div key={key} className="grid grid-cols-2 gap-6 mb-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">{label} ({unit})</Label>
                    <Input type="number" step={step}
                      value={editingBank?.costs?.[key] ?? ''}
                      onChange={(e) => setEditingBank({...editingBank, costs: {...editingBank?.costs, [key]: e.target.value === '' ? null : parseFloat(e.target.value)}})}
                      placeholder={placeholder} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">{label} ({unit})</Label>
                    <Input type="number" step={step}
                      value={editingBank?.costs_refinantare?.[key] ?? ''}
                      onChange={(e) => setEditingBank({...editingBank, costs_refinantare: {...editingBank?.costs_refinantare, [key]: e.target.value === '' ? null : parseFloat(e.target.value)}})}
                      placeholder={placeholder} />
                  </div>
                </div>
              ))}

              {/* Asigurări */}
              <h4 className="font-medium text-gray-700 mb-2 mt-4">Asigurări</h4>
              {[
                { key: 'life_insurance_percent', label: 'Asigurare viață', unit: '% lunar', step: '0.001', placeholder: '0.026' },
                { key: 'pad_fee', label: 'PAD / Asig. obligatorie', unit: 'RON/an', step: '1', placeholder: '130' },
                { key: 'property_insurance_percent', label: 'Asig. facultativă imobil', unit: '%', step: '0.001', placeholder: '0.11' },
              ].map(({ key, label, unit, step, placeholder }) => (
                <div key={key} className="grid grid-cols-2 gap-6 mb-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">{label} ({unit})</Label>
                    <Input type="number" step={step}
                      value={editingBank?.costs?.[key] ?? ''}
                      onChange={(e) => setEditingBank({...editingBank, costs: {...editingBank?.costs, [key]: e.target.value === '' ? null : parseFloat(e.target.value)}})}
                      placeholder={placeholder} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">{label} ({unit})</Label>
                    <Input type="number" step={step}
                      value={editingBank?.costs_refinantare?.[key] ?? ''}
                      onChange={(e) => setEditingBank({...editingBank, costs_refinantare: {...editingBank?.costs_refinantare, [key]: e.target.value === '' ? null : parseFloat(e.target.value)}})}
                      placeholder={placeholder} />
                  </div>
                </div>
              ))}

              {/* Alte costuri */}
              <h4 className="font-medium text-gray-700 mb-2 mt-4">Alte costuri</h4>
              {[
                { key: 'pmd_fee', label: 'PMD - Prima minimă PAD', unit: 'RON', step: '1', placeholder: '100' },
                { key: 'fngcimm_fee', label: 'FNGCIMM', unit: '%', step: '0.01', placeholder: '0.35' },
              ].map(({ key, label, unit, step, placeholder }) => (
                <div key={key} className="grid grid-cols-2 gap-6 mb-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">{label} ({unit})</Label>
                    <Input type="number" step={step}
                      value={editingBank?.costs?.[key] ?? ''}
                      onChange={(e) => setEditingBank({...editingBank, costs: {...editingBank?.costs, [key]: e.target.value === '' ? null : parseFloat(e.target.value)}})}
                      placeholder={placeholder} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">{label} ({unit})</Label>
                    <Input type="number" step={step}
                      value={editingBank?.costs_refinantare?.[key] ?? ''}
                      onChange={(e) => setEditingBank({...editingBank, costs_refinantare: {...editingBank?.costs_refinantare, [key]: e.target.value === '' ? null : parseFloat(e.target.value)}})}
                      placeholder={placeholder} />
                  </div>
                </div>
              ))}

              {/* Vouchere */}
              <h4 className="font-medium text-gray-700 mb-2 mt-4">Vouchere Intermediar</h4>
              {[
                { key: 'voucher_mortgage', label: 'Voucher Credit Ipotecar', unit: 'RON', step: '100', placeholder: '1000' },
                { key: 'voucher_refinance', label: 'Voucher Refinanțare', unit: 'RON', step: '100', placeholder: '500' },
              ].map(({ key, label, unit, step, placeholder }) => (
                <div key={key} className="grid grid-cols-2 gap-6 mb-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">{label} ({unit})</Label>
                    <Input type="number" step={step}
                      value={editingBank?.costs?.[key] ?? ''}
                      onChange={(e) => setEditingBank({...editingBank, costs: {...editingBank?.costs, [key]: e.target.value === '' ? null : parseFloat(e.target.value)}})}
                      placeholder={placeholder} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">{label} ({unit})</Label>
                    <Input type="number" step={step}
                      value={editingBank?.costs_refinantare?.[key] ?? ''}
                      onChange={(e) => setEditingBank({...editingBank, costs_refinantare: {...editingBank?.costs_refinantare, [key]: e.target.value === '' ? null : parseFloat(e.target.value)}})}
                      placeholder={placeholder} />
                  </div>
                </div>
              ))}
            </div>
            
            {/* Info box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <strong>ℹ️ Notă:</strong> Aceste date vor fi afișate automat în documentul FEIS la secțiunea "1. CREDITORUL".
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBankDialogOpen(false)}>Anulează</Button>
            <Button onClick={handleSaveBank} disabled={!editingBank?.name}>
              <Save className="h-4 w-4 mr-2" />
              Salvează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================ PRODUCT WIZARD ================ */}
      <Dialog open={isWizardOpen} onOpenChange={setIsWizardOpen}>
        <DialogContent 
          className="max-w-4xl max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>
              {editingProduct?.id ? 'Editare Produs' : 'Produs Nou'} - Pas {wizardStep}/3
            </DialogTitle>
            <DialogDescription>
              {wizardStep === 1 && 'Date Produs & Tip Dobândă'}
              {wizardStep === 2 && 'Dobânda Fixă & Marja Variabilă'}
              {wizardStep === 3 && 'Costuri & Finalizare'}
            </DialogDescription>
          </DialogHeader>

          {/* Progress bar */}
          <div className="flex gap-1 mb-6">
            {[1,2,3].map(step => (
              <div 
                key={step} 
                className={`h-2 flex-1 rounded ${step <= wizardStep ? 'bg-blue-500' : 'bg-gray-200'}`}
              />
            ))}
          </div>

          {editingProduct && (
            <div className="space-y-6">
              
              {/* ============ NEW STEP 1: INFO PRODUS & TIP DOBÂNDĂ ============ */}
              {wizardStep === 1 && (
                <div className="space-y-6">
                  {/* Bancă */}
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">Bancă *</Label>
                    <Select 
                      value={editingProduct.bank_id} 
                      onValueChange={handleBankSelect}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selectează banca" />
                      </SelectTrigger>
                      <SelectContent>
                        {banks.map(bank => (
                          <SelectItem key={bank.id} value={bank.id}>{bank.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Denumire produs */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold">Denumire Produs *</Label>
                      <Button type="button" size="sm" variant="outline" onClick={genProductName}
                        data-testid="wizard-gen-name" title="Construiește numele din selectoare (Tip, Dobândă, Fix ani, Clasă, Virare, Avans, Venit)">
                        <Sparkles className="h-3.5 w-3.5 mr-1" /> Generează nume
                      </Button>
                    </div>
                    <Input
                      value={editingProduct.product_name || ''}
                      onChange={(e) => updateProduct('product_name', e.target.value)}
                      placeholder="ex: Credit Ipotecar Standard"
                    />
                    <p className="text-xs text-gray-400">Sfat: setează mai întâi Tip/Dobândă/Clasă/Virare/Avans, apoi apasă „Generează nume" pentru o denumire standardizată (ca în vizualizarea Tabel).</p>
                  </div>

                  {/* Tip Credit */}
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">Tip Credit *</Label>
                    <div className="grid grid-cols-2 gap-4">
                      {CREDIT_TYPES.map(ct => (
                        <div 
                          key={ct.id}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            editingProduct.credit_type === ct.id 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => updateProduct('credit_type', ct.id)}
                        >
                          <div className="flex items-center gap-2">
                            {ct.id === 'achizitie' ? (
                              <Home className="h-6 w-6 text-blue-600" />
                            ) : (
                              <ArrowLeftRight className="h-6 w-6 text-green-600" />
                            )}
                            <span className="font-medium">{ct.label}</span>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{ct.labelRO}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Virare salariu (pt. denumire auto + calcul) */}
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">Virare salariu</Label>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { v: true, label: 'Cu virare' },
                        { v: false, label: 'Fără virare' },
                        { v: null, label: 'Nespecificat' },
                      ].map(opt => (
                        <div key={String(opt.v)}
                          className={`p-3 border-2 rounded-lg cursor-pointer text-center text-sm transition-all ${
                            editingProduct.salary_transfer === opt.v
                              ? 'border-blue-500 bg-blue-50 font-medium'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => updateProduct('salary_transfer', opt.v)}
                          data-testid={`wizard-virare-${String(opt.v)}`}
                        >
                          {opt.label}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Tip Dobândă */}
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">Tip Dobândă *</Label>
                    <div className="grid grid-cols-1 gap-3">
                      {INTEREST_TYPES.map(it => (
                        <div 
                          key={it.id}
                          className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                            editingProduct.interest_type === it.id 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => updateProduct('interest_type', it.id)}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full border-2 ${
                              editingProduct.interest_type === it.id 
                                ? 'border-blue-500 bg-blue-500' 
                                : 'border-gray-400'
                            }`}>
                              {editingProduct.interest_type === it.id && (
                                <Check className="w-3 h-3 text-white m-0.5" />
                              )}
                            </div>
                            <span className="font-medium">{it.label}</span>
                          </div>
                          <p className="text-sm text-gray-500 mt-1 ml-6">{it.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Perioadă fixă (doar dacă e fixed_then_variable) */}
                  {editingProduct.interest_type === 'fixed_then_variable' && (
                    <div className="space-y-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <Label className="font-semibold text-amber-800">Perioadă Dobândă Fixă *</Label>
                      <div className="flex gap-3">
                        {FIXED_PERIODS.map(fp => (
                          <Button
                            key={fp.years}
                            type="button"
                            variant={editingProduct.fixed_years === fp.years ? "default" : "outline"}
                            onClick={() => updateProduct('fixed_years', fp.years)}
                          >
                            {fp.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Clasă Energetică */}
                  <div className="space-y-3 p-4 bg-green-50 rounded-lg border border-green-200">
                    <Label className="font-semibold text-green-800">Clasă Energetică Imobil *</Label>
                    <div className="flex gap-3">
                      {ENERGY_CLASSES.map(ec => (
                        <Button
                          key={ec.id}
                          type="button"
                          variant={editingProduct.energy_class === ec.id ? "default" : "outline"}
                          className={editingProduct.energy_class === ec.id 
                            ? "bg-green-600 hover:bg-green-700" 
                            : "hover:border-green-400"
                          }
                          onClick={() => updateProduct('energy_class', ec.id)}
                        >
                          {ec.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Valută și Limite */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Valută</Label>
                      <Select 
                        value={editingProduct.currency || 'RON'} 
                        onValueChange={(v) => updateProduct('currency', v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="RON">RON</SelectItem>
                          <SelectItem value="EUR">EUR</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Sumă min ({editingProduct.currency})</Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={formatAmount(editingProduct.min_amount)}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (isValidAmountInput(val)) {
                            updateProduct('min_amount', parseAmount(val));
                          }
                        }}
                        placeholder="50.000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Sumă max ({editingProduct.currency})</Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={formatAmount(editingProduct.max_amount)}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (isValidAmountInput(val)) {
                            updateProduct('max_amount', parseAmount(val));
                          }
                        }}
                        placeholder="2.500.000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Perioadă max (luni)</Label>
                      <Input
                        type="number"
                        value={editingProduct.max_period_months || 360}
                        onChange={(e) => updateProduct('max_period_months', parseInt(e.target.value) || 360)}
                      />
                    </div>
                  </div>

                  {/* Încadrare Avans */}
                  <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <Label className="font-semibold text-blue-800">Încadrare după Avans (opțional)</Label>
                    <p className="text-sm text-blue-700 mb-2">
                      Stabilește pentru ce interval de avans apare produsul în calculator. Se citește <strong>atât din selector, cât și din denumire</strong>: dacă selectorul e „Nespecificat", calculatorul folosește încadrarea din titlu (ex. „Avans &lt;20%"). Produsele existente NU sunt afectate dacă nu modifici acest câmp.
                    </p>
                    {dpMismatch() && (
                      <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-300 text-red-800 text-sm" data-testid="product-down-payment-mismatch">
                        <AlertCircle className="w-5 h-5 mt-0.5 shrink-0 text-red-600" />
                        <div>
                          <strong>Atenție – nepotrivire avans!</strong> Denumirea indică <strong>{pairLabel(dpMismatch().title)}</strong>, dar selectorul este setat pe <strong>{pairLabel(dpMismatch().fields)}</strong>. Aliniază-le (schimbă selectorul sau denumirea) pentru a evita încadrări greșite în calculator.
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2 md:col-span-1">
                        <Label>Categorie avans</Label>
                        <Select value={dpSelectorKey()} onValueChange={onDpPresetChange}>
                          <SelectTrigger data-testid="product-down-payment-preset">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DP_PRESETS.map(p => (
                              <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {dpSelectorKey() === 'custom' && (
                        <>
                          <div className="space-y-2">
                            <Label>Avans min (%)</Label>
                            <Input
                              type="number"
                              value={editingProduct.down_payment_min ?? ''}
                              onChange={(e) => updateProduct('down_payment_min', e.target.value === '' ? null : parseFloat(e.target.value))}
                              placeholder="ex. 0"
                              data-testid="product-down-payment-min"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Avans max (%)</Label>
                            <Input
                              type="number"
                              value={editingProduct.down_payment_max ?? ''}
                              onChange={(e) => updateProduct('down_payment_max', e.target.value === '' ? null : parseFloat(e.target.value))}
                              placeholder="ex. 100"
                              data-testid="product-down-payment-max"
                            />
                          </div>
                        </>
                      )}
                    </div>
                    <p className="text-xs text-blue-600">
                      Regula din calculator: produsul apare dacă <strong>avans min ≤ avansul clientului &lt; avans max</strong>.
                    </p>
                  </div>

                  {/* Venit minim necesar */}
                  <div className="space-y-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <Label className="font-semibold text-amber-800">Venit Minim Necesar (Opțional)</Label>
                    <p className="text-sm text-amber-700 mb-2">
                      Dacă produsul necesită un venit minim, introduceți suma. Produsul NU va apărea în calculator pentru clienții cu venit mai mic.
                    </p>
                    <div className="flex items-center gap-3">
                      <Input
                        type="text"
                        inputMode="numeric"
                        className="w-40"
                        value={editingProduct.min_income_required ? formatAmount(editingProduct.min_income_required) : ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\./g, '');
                          if (val === '' || /^[0-9]+$/.test(val)) {
                            updateProduct('min_income_required', val === '' ? null : parseInt(val));
                          }
                        }}
                        placeholder="ex: 10.000"
                      />
                      <span className="text-amber-800 font-medium">lei net/lună</span>
                    </div>
                    {editingProduct.min_income_required > 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        Produsul va apărea doar pentru clienții cu venit {'>='} {formatAmount(editingProduct.min_income_required)} lei
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* ============ NEW STEP 2: RATA FIXĂ & MARJA VARIABILĂ ============ */}
              {wizardStep === 2 && (
                <div className="space-y-6">
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 text-sm text-blue-800">
                    <strong>Introduceți rata exactă pentru acest produs.</strong> Fiecare produs are o singură rată fixă și o singură marjă variabilă.
                  </div>

                  {/* Dobândă Fixă */}
                  {editingProduct.interest_type === 'fixed_then_variable' && (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <h3 className="font-semibold text-amber-800 mb-3">
                        Dobândă Fixă {editingProduct.fixed_years === 0 ? '(toată perioada)' : `(primii ${editingProduct.fixed_years} ani)`}
                      </h3>
                      <div className="flex items-center gap-3">
                        <Label className="min-w-[120px]">Rata fixă exactă:</Label>
                        <Input
                          type="text"
                          inputMode="decimal"
                          className="w-32"
                          data-testid="fixed-rate-input"
                          value={formatRO(editingProduct.fixed_rate)}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (isValidPercentInput(val)) {
                              updateProduct('fixed_rate', val);
                            }
                          }}
                          onBlur={(e) => {
                            const num = parseRO(e.target.value);
                            if (num !== null) {
                              updateProduct('fixed_rate', num);
                            }
                          }}
                          placeholder="5,79"
                        />
                        <span className="text-lg font-medium">%</span>
                      </div>
                    </div>
                  )}

                  {/* Dobândă Variabilă */}
                  {(editingProduct.interest_type === 'variable' || 
                    (editingProduct.interest_type === 'fixed_then_variable' && editingProduct.fixed_years !== 0)) && (
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <h3 className="font-semibold text-green-800 mb-3">
                        Marja Variabilă {editingProduct.interest_type === 'fixed_then_variable' ? `(după anul ${editingProduct.fixed_years})` : '(din prima zi)'}
                      </h3>
                      <div className="flex items-center gap-3 flex-wrap">
                        <Label className="min-w-[100px]">Formula:</Label>
                        <div className="flex items-center gap-2 bg-white p-2 rounded border">
                          <span className="font-mono font-bold text-blue-600">IRCC</span>
                          <span className="text-gray-500">({formatRO(irccRate)}%)</span>
                          <span className="text-xl font-bold">+</span>
                          <Input
                            type="text"
                            inputMode="decimal"
                            className="w-24"
                            data-testid="variable-margin-input"
                            value={formatRO(editingProduct.variable_margin)}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (isValidPercentInput(val)) {
                                updateProduct('variable_margin', val);
                              }
                            }}
                            onBlur={(e) => {
                              const num = parseRO(e.target.value);
                              if (num !== null) {
                                updateProduct('variable_margin', num);
                              }
                            }}
                            placeholder="2,50"
                          />
                          <span className="font-medium">% marjă</span>
                        </div>
                      </div>
                      {editingProduct.variable_margin && typeof editingProduct.variable_margin === 'number' && (
                        <p className="mt-2 text-green-700 text-sm">
                          Dobândă variabilă rezultată: <strong>{formatRO((irccRate + editingProduct.variable_margin).toFixed(2))}%</strong>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}


              {/* ============ NEW STEP 3: COSTURI & FINALIZARE ============ */}
              {wizardStep === 3 && (
                <div className="space-y-6">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-700">
                      Costurile se preiau automat din setările băncii. Pentru a le modifica, editați costurile din 
                      <strong> Costuri generale {banks.find(b => b.id === editingProduct.bank_id)?.name}</strong>.
                    </p>
                  </div>

                  {/* Costurile moștenite de la bancă - read only */}
                  {(() => {
                    const bank = banks.find(b => b.id === editingProduct.bank_id);
                    const isRefinantare = editingProduct.credit_type === 'refinantare';
                    const bankCostsBase = bank?.costs || {};
                    const bankCostsRefin = bank?.costs_refinantare || {};
                    const bankCosts = isRefinantare && Object.keys(bankCostsRefin).length > 0
                      ? { ...bankCostsBase, ...bankCostsRefin }
                      : bankCostsBase;

                    const CostDisplay = ({ label, value, unit }) => (
                      <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
                        <span className="text-sm text-gray-600">{label}</span>
                        <span className="text-sm font-medium">
                          {value != null && value !== '' ? (
                            <>{typeof value === 'number' ? formatRO(value) : value} {unit}</>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </span>
                      </div>
                    );

                    return (
                      <div className="bg-white border rounded-lg p-4 space-y-3">
                        <h3 className="font-semibold text-gray-700 text-sm">Comisioane</h3>
                        <CostDisplay label="Comision analiză dosar" value={bankCosts.analysis_fee} unit="RON" />
                        <CostDisplay label="Comision admin lunar" value={bankCosts.admin_fee_monthly} unit="RON" />
                        <CostDisplay label="Comision admin anual" value={bankCosts.admin_fee_annual} unit="RON" />
                        <CostDisplay label="Rambursare anticipată" value={bankCosts.early_repayment_fee} unit="%" />
                        
                        <h3 className="font-semibold text-gray-700 text-sm pt-2">Taxe</h3>
                        <CostDisplay label="RNPM" value={bankCosts.rnpm_fee} unit="RON" />
                        <CostDisplay label="Evaluare apartament" value={bankCosts.apartment_evaluation} unit="RON" />
                        <CostDisplay label="Evaluare casă" value={bankCosts.house_evaluation} unit="RON" />
                        
                        <h3 className="font-semibold text-gray-700 text-sm pt-2">Asigurări</h3>
                        <CostDisplay label="Asigurare viață" value={bankCosts.life_insurance_percent} unit="% lunar" />
                        <CostDisplay label="PAD / Asig. obligatorie" value={bankCosts.pad_fee} unit="RON/an" />
                        <CostDisplay label="Asig. facultativă imobil" value={bankCosts.property_insurance_percent} unit="%" />
                        
                        <h3 className="font-semibold text-gray-700 text-sm pt-2">Vouchere</h3>
                        <CostDisplay label="Voucher Credit Ipotecar" value={bankCosts.voucher_mortgage} unit="RON" />
                        <CostDisplay label="Voucher Refinanțare" value={bankCosts.voucher_refinance} unit="RON" />
                      </div>
                    );
                  })()}

                  {/* Sumar Produs */}
                  <div className="p-4 bg-gray-50 rounded-lg border">
                    <h3 className="font-semibold mb-3">Sumar Produs</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><strong>Bancă:</strong> {banks.find(b => b.id === editingProduct.bank_id)?.name}</div>
                      <div><strong>Denumire:</strong> {editingProduct.product_name}</div>
                      <div><strong>Tip:</strong> {CREDIT_TYPES.find(ct => ct.id === editingProduct.credit_type)?.label}</div>
                      <div><strong>Valută:</strong> {editingProduct.currency}</div>
                      <div><strong>Sumă:</strong> {formatAmount(editingProduct.min_amount)} - {formatAmount(editingProduct.max_amount)} {editingProduct.currency}</div>
                      <div><strong>Perioadă max:</strong> {editingProduct.max_period_months || 360} luni ({Math.round((editingProduct.max_period_months || 360)/12)} ani)</div>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t">
                      <strong>Dobânzi:</strong>
                      {editingProduct.interest_type === 'fixed_then_variable' ? (
                        <div className="mt-1">
                          {editingProduct.fixed_years === 0 ? (
                            <div>Fix toată perioada: <strong>{formatRO(editingProduct.fixed_rate)}%</strong></div>
                          ) : (
                            <>
                              <div>Fix {editingProduct.fixed_years} ani: <strong>{formatRO(editingProduct.fixed_rate)}%</strong></div>
                              <div>Variabil apoi: <strong>IRCC ({formatRO(irccRate)}%) + {formatRO(editingProduct.variable_margin)}% = {formatRO((irccRate + (parseRO(editingProduct.variable_margin) || 0)).toFixed(2))}%</strong></div>
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="mt-1">
                          <div>Variabil din start: <strong>IRCC ({formatRO(irccRate)}%) + {formatRO(editingProduct.variable_margin)}% = {formatRO((irccRate + (parseRO(editingProduct.variable_margin) || 0)).toFixed(2))}%</strong></div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Mențiuni */}
                  <div className="space-y-2">
                    <Label>Mențiuni / Note interne</Label>
                    <textarea
                      className="w-full p-3 border rounded-lg min-h-[100px]"
                      value={editingProduct.notes || ''}
                      onChange={(e) => updateProduct('notes', e.target.value)}
                      placeholder="Note interne despre acest produs..."
                    />
                  </div>

                  {/* Produs activ */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={editingProduct.active !== false}
                      onCheckedChange={(checked) => updateProduct('active', checked)}
                    />
                    <Label>Produs activ (vizibil în calculator)</Label>
                  </div>

                  {/* ============ SECȚIUNE COPIERE PRODUS ============ */}
                  {/* Buton Copiază din Achiziție - doar pentru Refinanțare */}
                  {editingProduct.credit_type === 'refinantare' && findAcquisitionProduct() && (
                    <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Copy className="h-6 w-6 text-amber-600 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="font-semibold text-amber-800">Copiază din Achiziție</h4>
                          <p className="text-sm text-amber-700 mt-1">
                            Există un produs de <strong>Achiziție</strong> pentru această bancă. 
                            Poți copia toate datele (dobânzi, costuri) din el.
                          </p>
                          <Button 
                            className="mt-3 bg-amber-500 hover:bg-amber-600 text-white"
                            onClick={() => {
                              setCopySourceProduct(findAcquisitionProduct());
                              setShowCopyDialog(true);
                            }}
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            Copiază datele din Achiziție
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Buton Copiază din Refinanțare - doar pentru Achiziție */}
                  {editingProduct.credit_type === 'achizitie' && findRefinanceProduct() && (
                    <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Copy className="h-6 w-6 text-amber-600 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="font-semibold text-amber-800">Copiază din Refinanțare</h4>
                          <p className="text-sm text-amber-700 mt-1">
                            Există un produs de <strong>Refinanțare</strong> pentru această bancă. 
                            Poți copia toate datele (dobânzi, costuri) din el.
                          </p>
                          <Button 
                            className="mt-3 bg-amber-500 hover:bg-amber-600 text-white"
                            onClick={() => {
                              setCopySourceProduct(findRefinanceProduct());
                              setShowCopyDialog(true);
                            }}
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            Copiază datele din Refinanțare
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Buton Sincronizare - doar când editezi un produs existent */}
                  {editingProduct.id && (
                    <>
                      {editingProduct.credit_type === 'refinantare' && findAcquisitionProduct() && (
                        <div className="p-4 bg-purple-50 border-2 border-purple-300 rounded-lg">
                          <div className="flex items-start gap-3">
                            <RefreshCw className="h-6 w-6 text-purple-600 mt-0.5" />
                            <div className="flex-1">
                              <h4 className="font-semibold text-purple-800">Sincronizează cu Achiziția</h4>
                              <p className="text-sm text-purple-700 mt-1">
                                Aplică modificările acestui produs și la produsul de <strong>Achiziție</strong>.
                              </p>
                              <Button 
                                variant="outline"
                                className="mt-3 border-purple-400 text-purple-700 hover:bg-purple-100"
                                onClick={() => setShowSyncDialog(true)}
                              >
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Sincronizează cu Achiziția
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                      {editingProduct.credit_type === 'achizitie' && findRefinanceProduct() && (
                        <div className="p-4 bg-purple-50 border-2 border-purple-300 rounded-lg">
                          <div className="flex items-start gap-3">
                            <RefreshCw className="h-6 w-6 text-purple-600 mt-0.5" />
                            <div className="flex-1">
                              <h4 className="font-semibold text-purple-800">Sincronizează cu Refinanțarea</h4>
                              <p className="text-sm text-purple-700 mt-1">
                                Aplică modificările acestui produs și la produsul de <strong>Refinanțare</strong>.
                              </p>
                              <Button 
                                variant="outline"
                                className="mt-3 border-purple-400 text-purple-700 hover:bg-purple-100"
                                onClick={() => setShowSyncDialog(true)}
                              >
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Sincronizează cu Refinanțarea
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex justify-between">
            <div>
              {wizardStep > 1 && (
                <Button variant="outline" onClick={prevStep}>
                  <ChevronLeft className="h-4 w-4 mr-1" /> Înapoi
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsWizardOpen(false)}>
                Anulează
              </Button>
              {wizardStep < 3 ? (
                <Button onClick={nextStep} disabled={!canGoNext()}>
                  Următorul <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSaveProduct}>
                  <Save className="h-4 w-4 mr-1" /> Salvează Produs
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Product Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmare ștergere produs</AlertDialogTitle>
            <AlertDialogDescription>
              Sigur doriți să ștergeți acest produs? Acțiunea nu poate fi anulată.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteProduct(deleteId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Șterge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Bank Confirmation */}
      <AlertDialog open={!!deleteBankId} onOpenChange={() => setDeleteBankId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmare ștergere bancă</AlertDialogTitle>
            <AlertDialogDescription>
              Sigur doriți să ștergeți această bancă? Toate produsele asociate vor fi de asemenea șterse. Acțiunea nu poate fi anulată.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleDeleteBank(deleteBankId);
                setDeleteBankId(null);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Șterge Banca
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Copy Product Confirmation Dialog */}
      <AlertDialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5 text-amber-500" />
              Confirmare copiere
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              Ești sigur că vrei să copiezi datele din produsul de <strong>{copySourceProduct?.credit_type === 'achizitie' ? 'Achiziție' : 'Refinanțare'}</strong>?
              <br /><br />
              <span className="text-amber-600 font-medium">
                Se vor suprascrie: dobânzile, discount-urile și costurile produsului curent.
              </span>
              <br />
              <span className="text-gray-500">
                Se păstrează: numele produsului și tipul creditului.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleCopyFromProduct(copySourceProduct)}
              className="bg-amber-500 hover:bg-amber-600"
            >
              <Copy className="h-4 w-4 mr-2" />
              Da, copiază datele
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sync Product Confirmation Dialog */}
      <AlertDialog open={showSyncDialog} onOpenChange={setShowSyncDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-purple-700">
              <RefreshCw className="h-5 w-5" />
              Confirmare sincronizare
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              <span className="text-red-600 font-semibold">⚠️ Atenție!</span>
              <br /><br />
              Modificările vor fi aplicate și la produsul de <strong>{editingProduct?.credit_type === 'refinantare' ? 'Achiziție' : 'Refinanțare'}</strong> al acestei bănci.
              <br /><br />
              <span className="text-purple-600 font-medium">
                Se vor sincroniza: dobânzile, discount-urile și costurile.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const targetProduct = editingProduct?.credit_type === 'refinantare' 
                  ? findAcquisitionProduct() 
                  : findRefinanceProduct();
                handleSyncToProduct(targetProduct);
              }}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Da, sincronizează
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BanksManagerV2;
